import {noop} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {
  ComposerAiFeature,
  ComposerAiFeatureTypesEnum,
} from '../../../../../../libs/shared/core/src/helpers/ai/composeV2Helpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

interface AiComposeGenerateDropdownProps {
  popoverTransferProps: PopoverTransferProps;
  onAiFeature: (feature: ComposerAiFeature) => void;
  onClickLearnMore: () => void;
  isTextTooShort: boolean;
}

/*
 * Style.
 */

const StyledGenerateModalBodyDiv = styled.div`
  width: 300px;
  padding: 20px 20px 8px;
  line-height: 22px;
`;

const StyledGenerateModalFooterDiv = styled.div`
  padding: 12px;
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
`;

const StyledActionCtaDiv = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledHeaderDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 55px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  color: ${(p) => p.theme.greys.shade80};
  text-align: center;
  font-weight: ${fontWeights.semibold};
  ${ellipsis()};
`;

/*
 * Component.
 */

export const AiComposeGenerateDropdown: FC<AiComposeGenerateDropdownProps> = ({
  popoverTransferProps,
  isTextTooShort,
  ...props
}) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Dropdown {...popoverTransferProps}>
    <StyledHeaderDiv>
      <Trans id="ai-compose-generate-dropdown-title">Compose with AI</Trans>
    </StyledHeaderDiv>
    <StyledGenerateModalBodyDiv>
      {isTextTooShort ? (
        <Trans id="ai-compose-generate-dropdown-description-tooshort">
          Outline your draft, then use AI to expand, translate, change tone, and more.
        </Trans>
      ) : (
        <Trans id="ai-compose-generate-dropdown-description">
          Generate a draft based on the conversation you’re replying to, or outline your email first and use
          AI to expand, simplify, change tones, and more.
        </Trans>
      )}
    </StyledGenerateModalBodyDiv>
    <StyledGenerateModalFooterDiv>
      <Button onClick={props.onClickLearnMore}>
        <StyledActionCtaDiv>
          <Trans id="ai-compose-generate-dropdown-learn-more-cta">Learn more</Trans>
          <Icon name="externalLinkSmall" />
        </StyledActionCtaDiv>
      </Button>
      <Button
        variant={ButtonVariantsEnum.PRIMARY}
        onClick={
          isTextTooShort
            ? noop
            : popoverTransferProps.wrap(() =>
                props.onAiFeature({type: ComposerAiFeatureTypesEnum.GENERATE_DRAFT}),
              )
        }
        isDisabled={isTextTooShort}
      >
        <Trans id="ai-compose-generate-dropdown-generate-cta">Generate</Trans>
      </Button>
    </StyledGenerateModalFooterDiv>
  </Dropdown>
);
